import React, { useState } from 'react';
import { ArrowRight, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Zap, SlidersHorizontal } from 'lucide-react';
import { ColumnMapping, ERP_PRESETS, ErpPreset } from '../../modules/erp/enterprise/migration/csvSpreadsheetParser';

export interface BatchSimulatorMappingStepProps {
  fileName: string;
  rawRowsCount: number;
  rawHeaders: string[];
  rawRows: Record<string, string>[];
  mappings: ColumnMapping;
  onMappingChange: (header: string, target: string) => void;
  onApplyPreset?: (presetId: ErpPreset['id']) => void;
  errorMessage: string | null;
  onBack: () => void;
  onProcessMapping: () => void;
}

export default function BatchSimulatorMappingStep({
  fileName,
  rawRowsCount,
  rawHeaders,
  rawRows,
  mappings,
  onMappingChange,
  onApplyPreset,
  errorMessage,
  onBack,
  onProcessMapping,
}: BatchSimulatorMappingStepProps) {
  const [showIgnored, setShowIgnored] = useState(false);
  const [activePreset, setActivePreset] = useState<ErpPreset['id']>('auto');

  // Segrega colunas mapeadas ativamente das ignoradas
  const mappedHeaders = rawHeaders.filter(h => mappings[h] && mappings[h] !== 'ignore');
  const ignoredHeaders = rawHeaders.filter(h => !mappings[h] || mappings[h] === 'ignore');

  const handleSelectPreset = (presetId: ErpPreset['id']) => {
    setActivePreset(presetId);
    if (onApplyPreset) {
      onApplyPreset(presetId);
    }
  };

  const renderColumnCard = (header: string) => {
    const currentTarget = mappings[header] || 'ignore';
    const isMapped = currentTarget !== 'ignore';

    return (
      <div 
        key={header} 
        className={`p-3 rounded-xl border transition-all space-y-1.5 ${
          isMapped 
            ? 'bg-emerald-50/40 border-emerald-200 ring-1 ring-emerald-500/20' 
            : 'bg-slate-50 border-slate-200'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {isMapped && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
            <span className="text-xs font-bold text-slate-800 truncate" title={header}>
              "{header}"
            </span>
          </div>
          <span className="text-[10px] text-slate-400 font-mono truncate max-w-[120px]">
            Ex: {rawRows[0]?.[header] || '-'}
          </span>
        </div>

        <select
          value={currentTarget}
          onChange={(e) => onMappingChange(header, e.target.value)}
          className={`w-full px-2.5 py-1.5 bg-white border rounded-lg text-xs font-medium focus:outline-none focus:ring-2 cursor-pointer ${
            isMapped 
              ? 'border-emerald-300 text-emerald-950 focus:ring-emerald-500' 
              : 'border-slate-300 text-slate-700 focus:ring-indigo-500'
          }`}
        >
          <option value="ignore">-- Ignorar esta coluna --</option>
          <optgroup label="Dados Principais">
            <option value="name">Descrição do Material / Insumo (Obrigatório)</option>
            <option value="sku">Código / SKU</option>
            <option value="itemType">Tipo de Item (Produto / Serviço)</option>
            <option value="sector">Segmento (Comércio / Serviço / Indústria)</option>
          </optgroup>
          <optgroup label="Classificação Fiscal">
            <option value="ncm">NCM (Mercadorias / Produtos Físicos)</option>
            <option value="nbs">NBS (Serviços / Intangíveis)</option>
            <option value="cfop">CFOP (Revenda / Uso & Consumo / Insumo)</option>
            <option value="fiscalCode">Código Fiscal Unificado (NCM ou NBS)</option>
            <option value="taxReduction">Redução / Benefício Fiscal (Reforma)</option>
          </optgroup>
          <optgroup label="Notas de Entrada (Compras)">
            <option value="costPrice">NF Entrada (Custo Total R$)</option>
            <option value="unitCost">Custo Unitário de Aquisição (R$)</option>
            <option value="legacyIcms">ICMS Destacado na NF de Entrada</option>
            <option value="legacyPis">PIS Destacado na NF de Entrada</option>
            <option value="legacyCofins">COFINS Destacado na NF de Entrada</option>
            <option value="legacyIss">ISS na Aquisição de Serviços</option>
            <option value="purchaseTermDays">Prazo Médio de Fornecedor (PMP em Dias)</option>
          </optgroup>
          <optgroup label="Notas de Saída (Vendas)">
            <option value="salePrice">Preço de Venda Praticado (Saída R$)</option>
            <option value="paymentTermDays">Prazo Médio de Clientes (PMR em Dias)</option>
          </optgroup>
        </select>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-4">
        <div>
          <span className="text-xs font-black uppercase tracking-wider text-emerald-800">
            Arquivo Carregado: {fileName} ({rawRowsCount} linhas • {rawHeaders.length} colunas)
          </span>
          <h3 className="text-lg font-black text-slate-900 mt-0.5">
            Mapeamento Inteligente de Colunas (De / Para Multi-Plataforma)
          </h3>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="text-xs font-bold text-slate-500 hover:text-slate-800 cursor-pointer"
        >
          Trocar arquivo
        </button>
      </div>

      {/* Barra de Presets de ERPs de Mercado */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-slate-700" />
            <span className="text-xs font-black text-slate-800 uppercase tracking-wide">
              Presets de Plataformas & ERPs de Mercado
            </span>
          </div>
          <span className="text-[11px] text-slate-500">
            {mappedHeaders.length} de {rawHeaders.length} colunas identificadas
          </span>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          {ERP_PRESETS.map((preset) => {
            const isSelected = activePreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleSelectPreset(preset.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                  isSelected
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'
                }`}
                title={preset.description}
              >
                {preset.id === 'auto' && <Zap className="w-3 h-3 text-amber-400" />}
                <span>{preset.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-slate-600 leading-relaxed">
        Confira abaixo os campos fiscais identificados. O R-MAX já padronizou os nomes das colunas de compras, tributos e catálogo de faturamento:
      </p>

      {/* 1. Bloco de Colunas Mapeadas */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-black text-emerald-800 uppercase tracking-wide flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Colunas Principais Mapeadas ({mappedHeaders.length})
          </span>
        </div>

        {mappedHeaders.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {mappedHeaders.map(renderColumnCard)}
          </div>
        ) : (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
            Nenhuma coluna foi mapeada automaticamente. Escolha um preset acima ou mapeie manualmente.
          </div>
        )}
      </div>

      {/* 2. Bloco Recolhível de Colunas Ignoradas (para planilhas com 56+ colunas) */}
      {ignoredHeaders.length > 0 && (
        <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
          <button
            type="button"
            onClick={() => setShowIgnored(!showIgnored)}
            className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between text-left cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-700">
                Outras Colunas do Arquivo ({ignoredHeaders.length} ignoradas ou secundárias)
              </span>
              <span className="text-[10px] text-slate-400 bg-white px-2 py-0.5 rounded-full border border-slate-200">
                Opcional
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs font-semibold text-slate-500">
              <span>{showIgnored ? 'Ocultar' : 'Revisar / Mapear'}</span>
              {showIgnored ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </button>

          {showIgnored && (
            <div className="p-4 border-t border-slate-200 bg-slate-50/50">
              <p className="text-[11px] text-slate-500 mb-3">
                Estas colunas não são obrigatórias para a apuração da CBS/IBS, mas você pode associá-las caso deseje:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ignoredHeaders.map(renderColumnCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {errorMessage && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Rodapé com botões de ação */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={onProcessMapping}
          className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md shadow-emerald-600/20 flex items-center gap-2 cursor-pointer"
        >
          <span>Calcular e Iniciar Simulação</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
